import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { useSentinelTheme } from "../contexts/ThemeContext";

const ESTADO_COLOR = {
  Nueva: "#0A84FF",
  Asignada: "#006341",
  "En curso": "#FF9F0A",
  Resuelta: "#7AB800",
  Verificada: "#10b981",
  Cerrada: "#6b7280",
  Rechazada: "#FF3B30",
  Duplicada: "#6b7280",
};

function HeatLayer({ points, gradient }) {
  const map = useMap();
  useEffect(() => {
    if (!points?.length) return;
    const layer = L.heatLayer(points, { radius: 22, blur: 18, maxZoom: 16, gradient: gradient || { 0.2: "#0A84FF", 0.5: "#7AB800", 0.8: "#FF9F0A", 1: "#FF3B30" } });
    layer.addTo(map);
    return () => map.removeLayer(layer);
  }, [map, points, gradient]);
  return null;
}

const RIESGO_COLOR = {
  alto: "#FF3B30",
  medio: "#FF9F0A",
  bajo: "#7AB800",
  sin_datos: "#6b7280",
};

function esriPolylineToLatLngs(geom) {
  if (!geom?.paths) return [];
  return geom.paths.flatMap((path) => path.map(([x, y]) => [y, x]));
}

export function MapView({ heatmapPoints = [], incidents = [], sectores = [], barrios = [], fieldPositions = [], routeGeometry = null, coverageZones = [], showHeatmap = true, showIncidents = true, showSectors = true, height = "480px" }) {
  const { salaControl } = useSentinelTheme();
  const tileUrl = salaControl
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

  return (
    <div style={{ height }} className="w-full overflow-hidden rounded-lg border border-border relative z-0" data-testid="map-view">
      <MapContainer center={[41.451, 2.176]} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
        <TileLayer url={tileUrl} attribution='&copy; OpenStreetMap &copy; CartoDB' />
        {showSectors &&
          sectores.map((s) => {
            const color = RIESGO_COLOR[s.nivel_riesgo] || RIESGO_COLOR.sin_datos;
            return (
              <Polygon
                key={s._id}
                positions={s.geometry.coordinates[0].map(([lon, lat]) => [lat, lon])}
                pathOptions={{ color, weight: 1.5, fillColor: color, fillOpacity: 0.18 }}
                data-testid={`sector-polygon-${s._id}`}
              >
                <Tooltip>
                  {s.nombre}
                  {s.indice_riesgo !== undefined && ` — índice de riesgo: ${s.indice_riesgo}`}
                </Tooltip>
              </Polygon>
            );
          })}
        {showSectors &&
          barrios.map((b) => (
            <Polygon
              key={b._id}
              positions={b.geometry.coordinates[0].map(([lon, lat]) => [lat, lon])}
              pathOptions={{ color: "#006341", weight: 1, dashArray: "3 3", fillOpacity: 0 }}
            >
              <Tooltip>{b.nombre}</Tooltip>
            </Polygon>
          ))}
        {showHeatmap && <HeatLayer points={heatmapPoints} />}
        {coverageZones.length > 0 && (
          <HeatLayer
            points={coverageZones.map((z) => [z.lat, z.lon, Math.min(z.veces, 5)])}
            gradient={{ 0.2: "#FFD60A", 0.6: "#FF9F0A", 1: "#FF3B30" }}
          />
        )}
        {routeGeometry && (
          <Polyline positions={esriPolylineToLatLngs(routeGeometry)} pathOptions={{ color: "#7AB800", weight: 4, opacity: 0.85 }} data-testid="route-real-geometry" />
        )}
        {fieldPositions
          .filter((p) => p.historial?.length > 1)
          .map((p) => (
            <Polyline
              key={`trail-${p.user_id}`}
              positions={p.historial.map((h) => [h.lat, h.lon])}
              pathOptions={{ color: "#0A84FF", weight: 2, opacity: 0.5, dashArray: "4 6" }}
              data-testid={`field-trail-${p.user_id}`}
            />
          ))}
        {fieldPositions
          .filter((p) => p.lat && p.lon)
          .map((p) => {
            const staleMs = p.actualizado_en ? Date.now() - new Date(p.actualizado_en).getTime() : Infinity;
            const stale = staleMs > 15 * 60 * 1000;
            const color = stale ? "#6b7280" : "#0A84FF";
            return (
              <CircleMarker
                key={p.user_id}
                center={[p.lat, p.lon]}
                radius={9}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.9, weight: 2 }}
                data-testid={`field-position-${p.user_id}`}
              >
                <Tooltip>
                  {p.nombre} {stale ? "(sin señal reciente)" : ""}
                  <br />
                  {p.actualizado_en ? `Actualizado: ${new Date(p.actualizado_en).toLocaleTimeString("es-ES")}` : "Sin posición registrada"}
                </Tooltip>
              </CircleMarker>
            );
          })}
        {showIncidents &&
          incidents.map((inc) => (
            <CircleMarker
              key={inc._id}
              center={[inc.lat, inc.lon]}
              radius={6}
              pathOptions={{ color: ESTADO_COLOR[inc.estado] || "#7AB800", fillOpacity: 0.85, weight: 1 }}
            >
              <Tooltip>
                {inc.codigo} — {inc.estado}
              </Tooltip>
            </CircleMarker>
          ))}
      </MapContainer>
      {showSectors && sectores.some((s) => s.nivel_riesgo) && (
        <div className="absolute bottom-3 left-3 z-[400] flex items-center gap-3 rounded-md bg-card/90 backdrop-blur border border-border px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider" data-testid="sector-risk-legend">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: RIESGO_COLOR.alto }} /> Alto
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: RIESGO_COLOR.medio }} /> Medio
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: RIESGO_COLOR.bajo }} /> Bajo
          </span>
        </div>
      )}
    </div>
  );
}
